import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Linkedin } from 'lucide-react';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumb from '../components/seo/Breadcrumb';
import { TEAM } from '../data/team';
import { isEnglishPath } from '../utils/i18nRoutes';

const ALTERNATES = [
  { hreflang: 'es', href: '/equipo' },
  { hreflang: 'en', href: '/en/team' },
  { hreflang: 'x-default', href: '/equipo' },
];

const TEXT = {
  es: {
    seoTitle: 'Equipo de innovación | Valoriza',
    seoDescription: 'Quién diseña y pone en marcha la innovación de Valoriza: IA, I+D, open innovation y despliegue en campo.',
    tag: 'El equipo',
    h1a: 'Las personas que ponen esto en la',
    h1b: 'calle',
    breadcrumb: [{ label: 'Inicio', to: '/' }, { label: 'Equipo' }],
  },
  en: {
    seoTitle: 'Innovation Team | Valoriza',
    seoDescription: 'Who designs and runs innovation at Valoriza: AI, R&D, open innovation and field deployment.',
    tag: 'The team',
    h1a: 'The people putting this on the',
    h1b: 'street',
    breadcrumb: [{ label: 'Home', to: '/en' }, { label: 'Team' }],
  },
};

export default function EquipoPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const locale = isEnglishPath(pathname) ? 'en' : 'es';
  const t = TEXT[locale];
  const path = locale === 'en' ? '/en/team' : '/equipo';
  const to = (slug) => (locale === 'en' ? `/en/team/${slug}` : `/equipo/${slug}`);

  return (
    <>
      <SEOHead title={t.seoTitle} description={t.seoDescription} path={path} locale={locale} alternates={ALTERNATES} />
      <div className="pt-32 pb-24 v-container" data-testid="equipo-page">
        <Breadcrumb items={t.breadcrumb} />

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-2xl mb-14">
          <span className="v-tag green mb-6 inline-flex">{t.tag}</span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black dark:text-white/95 mb-4 tracking-tight" data-testid="equipo-h1">
            {t.h1a}{' '}
            <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: '#8DC21F' }}>{t.h1b}</span>
          </h1>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TEAM.map((member, i) => {
            const Icon = member.icon;
            const role = locale === 'en' ? member.roleEn : member.roleEs;
            const bio = locale === 'en' ? member.bioEn : member.bioEs;
            const card = (
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                whileHover={{ y: -8 }}
                onClick={() => navigate(to(member.slug))}
                className="liquid-glass rounded-3xl overflow-hidden group relative cursor-pointer"
              >
                <div className="relative h-40 flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${member.color}15 0%, ${member.color}05 100%)` }}>
                  <div className="w-24 h-24 rounded-full flex items-center justify-center overflow-hidden" style={{ background: `${member.color}20`, border: `3px solid ${member.color}40` }}>
                    {member.photo ? (
                      <img src={member.photo} alt={`${member.name}, ${role}`} className="w-full h-full object-cover" />
                    ) : (
                      <Icon size={36} style={{ color: member.color }} />
                    )}
                  </div>
                  <a
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 dark:bg-black/90 flex items-center justify-center shadow-lg"
                    style={{ border: `2px solid ${member.color}` }}
                    data-testid={`team-linkedin-${member.slug}`}
                  >
                    <Linkedin size={16} style={{ color: member.color }} />
                  </a>
                </div>
                <div className="p-5">
                  <h2 className="text-base font-bold text-black dark:text-white/90 mb-1">{member.name}</h2>
                  <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: member.color }}>{role}</p>
                  <p className="text-sm text-gray-700 dark:text-white/65 leading-relaxed">{bio}</p>
                </div>
              </motion.div>
            );
            return (
              <div key={member.slug} data-testid={`team-card-${member.slug}`}>{card}</div>
            );
          })}
        </div>
      </div>
    </>
  );
}
